import React from "react";

import {
  Breakpoints,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextType,
  ThemeProvider
} from "@vw-marketing/us-components";

import { formatPhoneNumber, getPrimaryPhone, handleAdditionalContactsMutation } from "../../../../shared/utils/general";
import { ManageContactsAddEditEmailView } from "../manage-contacts-add-edit-email/ManageContactsAddEditEmailView";
import { ManageContactsAddEditPhoneView } from "../manage-contacts-add-edit-phone/ManageContactsAddEditPhoneView";
import { ManageContactsAlerts } from "../manage-contacts-alerts/ManageContactsAlerts";
import { ManageContactsMutation, ManageContactsPayload, UserData } from "../../../../shared/typings/main";
import { ManageContactsRemoveEmailView } from "../manage-contacts-remove-email/ManageContactsRemoveEmailView";
import { ManageContactsRemovePhoneView } from "../manage-contacts-remove-phone/ManageContactsRemovePhoneView";
import { onVWBasicButtonClickWithProcessEvent } from "../../../../shared/tagging/tagging-helpers";
import { StyledDescription, StyledManageContacts, StyledAlerts } from "./styles";

interface ManageContactsViewProps {
  user: UserData | undefined;
  trackingManager: any;
  translationHandler: (key: string) => string;
  setTitle: (title: string) => void;
  setContent: (content: React.ReactElement) => void;
  setGoBack: (fn: (() => void) | undefined) => void;
  onClose: () => void;
  onChange: () => void;
  onResetChanges: () => void;
  onSaveSuccess: (message: string) => void;
  onSaveError: (message: string) => void;
  fetchSaveAdditionalContacts: (payload: ManageContactsPayload) => Promise<void>;
}

export const ManageContactsView: React.FC<ManageContactsViewProps> = ({
  user,
  trackingManager,
  translationHandler,
  setTitle,
  setContent,
  setGoBack,
  onClose,
  onChange,
  onResetChanges,
  onSaveSuccess,
  onSaveError,
  fetchSaveAdditionalContacts,
}) => {

  const primaryEmail = user?.email ?? '';
  const additionalEmails = (user?.emails ?? [])
    .filter(data => !data.primary)
    .slice(0, 2)
    .map(data => data.email)

  const initialPhoneNumber: string = getPrimaryPhone(user ?? ({} as any));
  const primaryPhoneNumber: string = formatPhoneNumber(initialPhoneNumber).formatted;
  const additionalPhoneNumbers = (user?.phones ?? [])
    .filter(data => !data.primary)
    .slice(0, 2)
    .map(data => data.number);
  const additionalPhoneNumberFormatted = additionalPhoneNumbers.map(data => formatPhoneNumber(data).formatted);

  const trackLinkClick = (linkName: string) => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Manage Contacts',
        StepNumber: '4.5',
      }, {
        name: linkName,
      });
    } catch (error) {
      console.warn('Error tracking link click:', error);
    }
  };

  const handleGoBack = () => {
    trackLinkClick('Go Back');
    onResetChanges();
    setGoBack(undefined);
    setTitle(translationHandler('accountSettings.manage.contacts.title'));
    setContent(
      <ThemeProvider theme="main">
        <ManageContactsView
          user={user}
          trackingManager={trackingManager}
          translationHandler={translationHandler}
          setTitle={setTitle}
          setContent={setContent}
          setGoBack={setGoBack}
          onClose={onClose}
          onChange={onChange}
          onResetChanges={onResetChanges}
          onSaveSuccess={onSaveSuccess}
          onSaveError={onSaveError}
          fetchSaveAdditionalContacts={fetchSaveAdditionalContacts}
        />
      </ThemeProvider>
    );
  };

  const handleAddEditEmail = (flow: 'add' | 'edit', initialEmail?: string) => {
    trackLinkClick(flow == 'add' ? 'Add email' : 'Edit email');

    const titleKey = flow === 'add'
      ? 'accountSettings.manage.contacts.interaction.layer.email.alerts.add.email.title'
      : 'accountSettings.manage.contacts.interaction.layer.email.alerts.edit.email.title';
    const successKey = flow === 'add'
      ? 'accountSettings.manage.contacts.email.toast.add.success'
      : 'accountSettings.manage.contacts.email.toast.edit.success';
    const errorKey = flow === 'add'
      ? 'accountSettings.manage.contacts.email.toast.add.error'
      : 'accountSettings.manage.contacts.email.toast.edit.error';
    setGoBack(() => handleGoBack);
    setTitle(translationHandler(titleKey));
    setContent(
      <ThemeProvider theme="main">
        <ManageContactsAddEditEmailView
          trackingManager={trackingManager}
          flow={flow}
          onSave={(mutation) => handleAdditionalContactsSaveOrUpdate(mutation, 'email')}
          onSuccess={() => { onSaveSuccess(translationHandler(successKey)); onClose(); }}
          onError={() => { onSaveError(translationHandler(errorKey)); onClose(); }}
          onChange={onChange}
          onResetChanges={onResetChanges}
          translationHandler={translationHandler}
          initialEmail={initialEmail}
          existingEmails={(user?.emails ?? []).map(data => data.email)}
        />
      </ThemeProvider>
    );
  };

  const handleAddEditPhone = (flow: 'add' | 'edit', initialPhoneNumber?: string) => {
    trackLinkClick(flow == 'add' ? 'Add mobile number' : 'Edit mobile number');

    const titleKey = flow === 'add'
      ? 'accountSettings.manage.contacts.interaction.layer.sms.alerts.add.phone.title'
      : 'accountSettings.manage.contacts.interaction.layer.sms.alerts.edit.phone.title';
    const successKey = flow === 'add'
      ? 'accountSettings.manage.contacts.phone.toast.add.success'
      : 'accountSettings.manage.contacts.phone.toast.edit.success';
    const errorKey = flow === 'add'
      ? 'accountSettings.manage.contacts.phone.toast.add.error'
      : 'accountSettings.manage.contacts.phone.toast.edit.error';
    setGoBack(() => handleGoBack);
    setTitle(translationHandler(titleKey));
    setContent(
      <ThemeProvider theme="main">
        <ManageContactsAddEditPhoneView
          trackingManager={trackingManager}
          flow={flow}
          onSave={(mutation) => handleAdditionalContactsSaveOrUpdate(mutation, 'mobileNumber')}
          onSuccess={() => { onSaveSuccess(translationHandler(successKey)); onClose(); }}
          onError={() => { onSaveError(translationHandler(errorKey)); onClose(); }}
          onChange={onChange}
          onResetChanges={onResetChanges}
          translationHandler={translationHandler}
          initialPhoneNumber={initialPhoneNumber}
          existingNumbers={(user?.phones ?? []).map(data => data.number)}
        />
      </ThemeProvider>
    );
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    trackLinkClick('Remove email address');

    const successKey = "accountSettings.manage.contacts.email.toast.remove.success";
    const errorKey = "accountSettings.manage.contacts.email.toast.remove.error";
    setGoBack(() => handleGoBack);
    setTitle(translationHandler('accountSettings.manage.contacts.interaction.layer.email.alerts.remove.email.title'));
    setContent(
      <ThemeProvider theme="main">
        <ManageContactsRemoveEmailView
          trackingManager={trackingManager}
          emailToRemove={emailToRemove}
          onRemove={(mutation) => handleAdditionalContactsSaveOrUpdate(mutation, 'email')}
          onSuccess={() => { onSaveSuccess(translationHandler(successKey)); onClose(); }}
          onError={() => { onSaveError(translationHandler(errorKey)); onClose(); }}
          handleAddEditEmail={handleAddEditEmail}
          translationHandler={translationHandler}
        />
      </ThemeProvider>
    );
  };

  const handleRemovePhone = (phoneToRemove: string) => {
    trackLinkClick('Remove mobile number');

    const successKey = "accountSettings.manage.contacts.phone.toast.remove.success";
    const errorKey = "accountSettings.manage.contacts.phone.toast.remove.error";
    setGoBack(() => handleGoBack);
    setTitle(translationHandler('accountSettings.manage.contacts.interaction.layer.sms.alerts.remove.phone.title'));
    setContent(
      <ThemeProvider theme="main">
        <ManageContactsRemovePhoneView
          trackingManager={trackingManager}
          phoneToRemove={phoneToRemove}
          onRemove={(mutation) => handleAdditionalContactsSaveOrUpdate(mutation, 'mobileNumber')}
          onSuccess={() => { onSaveSuccess(translationHandler(successKey)); onClose(); }}
          onError={() => { onSaveError(translationHandler(errorKey)); onClose(); }}
          handleAddEditPhone={handleAddEditPhone}
          translationHandler={translationHandler}
        />
      </ThemeProvider>
    );
  };

  const handleAdditionalContactsSaveOrUpdate = async (additionalContact: ManageContactsMutation, contactType: 'email' | 'mobileNumber') => {
    let emails = [...additionalEmails];
    let phones = [...additionalPhoneNumbers];

    if (contactType === 'email') {
      emails = handleAdditionalContactsMutation(emails, additionalContact);
    } else if (contactType === 'mobileNumber') {
      phones = handleAdditionalContactsMutation(phones, additionalContact);
    }

    const payload: ManageContactsPayload = {
      firstName: {
        value: user?.firstName ?? ""
      },
      lastName: {
        value: user?.lastName ?? ""
      },
      primaryPhone: initialPhoneNumber,
      emails: [...emails],
      phones: [...phones]
    }

    await fetchSaveAdditionalContacts(payload);
  };

  return (
    <ThemeProvider theme="main">
      <StyledManageContacts>
        <StyledDescription>
          <TextCustom
            appearance={TextAppearance.copy200}
            style={{
              [Breakpoints.default]: {
                color: TextColor.inherit,
                fontSize: TextSize.TextSize16,
                fontType: TextType.Copy,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize20,
                lineHeight: TextLineHeight.TextLineHeight32,
              },
            }}
          >
            {translationHandler("accountSettings.manage.contacts.interaction.layer.description")}
          </TextCustom>
        </StyledDescription>
        <StyledAlerts>
          <ManageContactsAlerts
            title={translationHandler("accountSettings.manage.contacts.interaction.layer.email.alerts.title")}
            primaryAlertText={translationHandler("accountSettings.manage.contacts.interaction.layer.email.alerts.primary.title")}
            additionalAlertText={translationHandler("accountSettings.manage.contacts.interaction.layer.email.alerts.additional.title")}
            addCTAText={translationHandler("accountSettings.manage.contacts.interaction.layer.email.alerts.cta")}
            translationHandler={translationHandler}
            additionalContent={additionalEmails}
            primaryContent={primaryEmail}
            handleAddEdit={handleAddEditEmail}
            handleRemove={handleRemoveEmail}
          />
          <ManageContactsAlerts
            title={translationHandler("accountSettings.manage.contacts.interaction.layer.sms.alerts.title")}
            primaryAlertText={translationHandler("accountSettings.manage.contacts.interaction.layer.sms.alerts.primary.title")}
            additionalAlertText={translationHandler("accountSettings.manage.contacts.interaction.layer.sms.alerts.additional.title")}
            addCTAText={translationHandler("accountSettings.manage.contacts.interaction.layer.sms.alerts.cta")}
            translationHandler={translationHandler}
            additionalContent={additionalPhoneNumberFormatted}
            primaryContent={primaryPhoneNumber}
            handleAddEdit={handleAddEditPhone}
            handleRemove={handleRemovePhone}
          />
        </StyledAlerts>
      </StyledManageContacts>
    </ThemeProvider>
  );
}